import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { wsService } from '@/services/websocket';
import { ticketsApi, usersApi, tasksApi, internalChatApi } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import { AnimatePresence, motion } from 'framer-motion';
import notificationSound from '@/assets/notification.mp3';
import type { UserPermissions } from '@/types';
import LanguageSwitcher from './LanguageSwitcher';
import {
  X,
  User as UserIcon,
  Phone,
  Menu,
  ShieldCheck,
} from 'lucide-react';

import {
  // Main navigation
  Squares2X2Icon,
  ChatBubbleLeftRightIcon,
  BookOpenIcon,
  SparklesIcon,
  BoltIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CalendarDaysIcon,
  InboxStackIcon,
  // Secondary navigation
  DocumentTextIcon,
  WifiIcon,
  ShieldCheckIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  BookmarkIcon,
  ArrowsRightLeftIcon,
  CpuChipIcon,
  AcademicCapIcon,
  PencilSquareIcon,
  BuildingOffice2Icon,
  LinkIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  TagIcon,
  // Sidebar controls
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  EllipsisVerticalIcon,
  SunIcon,
  MoonIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';

// Helper function to check if user has a specific permission
// Global type declaration for audio message playback tracking
// This flag is set by AudioPlayer component to prevent notification sounds from interrupting audio playback
declare global {
  interface Window {
    __audioMessagePlaying?: boolean;
  }
}

const hasPermission = (permissions: UserPermissions | undefined, permission: keyof UserPermissions): boolean => {
  if (!permissions) return false;
  const value = permissions[permission];
  return typeof value === 'boolean' ? value : false;
};

// Check if user is admin (they have all permissions)
const isAdminRole = (role: string | undefined): boolean => role === 'admin';

// Tooltip component for sidebar menu items - uses portal to render on top of everything
const SidebarTooltip = memo(({ children, label, show }: { children: React.ReactNode; label: string; show: boolean }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8,
      });
    }
  }, []);

  if (!show) {
    return <>{children}</>;
  }

  return (
    <div
      ref={ref}
      onMouseEnter={() => { updatePosition(); setIsHovered(true); }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {isHovered && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, x: -4, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -4, scale: 0.95 }}
            transition={{ duration: 0.12 }}
            className="fixed pointer-events-none"
            style={{
              top: position.top,
              left: position.left,
              transform: 'translateY(-50%)',
              zIndex: 999999,
            }}
          >
            <div
              className="px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap shadow-lg"
              style={{
                backgroundColor: 'var(--text-primary)',
                color: 'var(--bg-primary)',
              }}
            >
              {label}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
});

// Helper component for user avatar - memoized to prevent unnecessary re-renders
const UserAvatar = memo(({ user, size = 'md', className = '' }: { user: any; size?: 'sm' | 'md' | 'lg'; className?: string }) => {
  // Detect placeholder strings from build and use fallback
  const envApiUrl = process.env.REACT_APP_API_URL;
  const API_BASE = (envApiUrl && !envApiUrl.startsWith('__'))
    ? envApiUrl
    : (typeof window !== 'undefined' ? window.location.origin : '');
  const sizeClasses = {
    sm: 'w-9 h-9 text-sm',
    md: 'w-12 h-12 text-xl',
    lg: 'w-16 h-16 text-2xl'
  };

  const avatarUrl = user?.profilePicUrl
    ? (user.profilePicUrl.startsWith('http') ? user.profilePicUrl : `${API_BASE}${user.profilePicUrl}`)
    : null;

  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-semibold overflow-hidden ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor: avatarUrl ? 'transparent' : 'var(--wa-green)', flexShrink: 0 }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={user?.name || 'Avatar'} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        user?.name?.[0]?.toUpperCase() || 'U'
      )}
    </div>
  );
});

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUser, companySettings } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { t } = useTranslation('common');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  // Menu expande baseado na preferência do usuário (sempre aberto ou sempre fechado)
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('chatvono_sound_enabled');
    if (stored !== null) return stored === 'true';
    return user?.soundEnabled ?? true;
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const menuCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileNameInputRef = useRef<HTMLInputElement | null>(null);
  const moreMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const overflowMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const sidebarNavRef = useRef<HTMLDivElement | null>(null);
  const [visibleSecondaryCount, setVisibleSecondaryCount] = useState<number>(999);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Update soundEnabled when user changes (only if no local preference stored)
  useEffect(() => {
    const stored = localStorage.getItem('chatvono_sound_enabled');
    if (stored === null) {
      setSoundEnabled(user?.soundEnabled ?? true);
    }
  }, [user?.soundEnabled]);

  // Clear timeout helper
  const clearMenuTimeout = useCallback(() => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current);
      menuCloseTimeoutRef.current = null;
    }
  }, []);

  // Handle menu open/close
  const handleMenuOpen = useCallback(() => {
    clearMenuTimeout();
    if (moreMenuButtonRef.current) {
      const rect = moreMenuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top,
        left: rect.right + 8
      });
    }
    setShowMoreMenu(true);
  }, [clearMenuTimeout]);

  const handleMenuClose = useCallback(() => {
    clearMenuTimeout();
    setShowMoreMenu(false);
  }, [clearMenuTimeout]);

  const handleToggleSidebar = useCallback(async () => {
    const next = !sidebarExpanded;
    setSidebarExpanded(next);
    if (user) {
      updateUser({ ...user, menuExpanded: next });
    }
    try {
      await usersApi.updateMenuPreference(next);
    } catch (e) {
      console.error('Failed to save sidebar preference', e);
    }
  }, [sidebarExpanded, user, updateUser]);

  // Permission check helper that includes admin override
  const canAccess = useCallback((permission: keyof UserPermissions): boolean => {
    if (isAdminRole(user?.role)) return true;
    return hasPermission(user?.permissions, permission);
  }, [user?.role, user?.permissions]);

  // Check if user is super admin (company 1 admin)
  const isSuperAdmin = user?.companyId === 1 && user?.role === 'admin';

  // Module check helper - verifies if a module is enabled for the company
  // Super admin always has access to all modules
  type ModuleType = 'contacts' | 'connections' | 'quickReplies' | 'queues' | 'tags' | 'users' | 'crm' | 'flowBuilder' | 'scheduledMessages' | 'campaigns' | 'webhooks' | 'knowledgeBase' | 'aiAgent' | 'integrations' | 'kanban';

  const isModuleEnabled = useCallback((module: ModuleType): boolean => {
    if (isSuperAdmin) return true;
    if (!companySettings) return false;
    switch (module) {
      case 'contacts': return companySettings.enableContacts !== false;
      case 'connections': return companySettings.enableConnections !== false;
      case 'quickReplies': return companySettings.enableQuickReplies !== false;
      case 'queues': return companySettings.enableQueues !== false;
      case 'tags': return companySettings.enableTags !== false;
      case 'users': return companySettings.enableUsers !== false;
      case 'crm': return companySettings.enableCRM === true;
      case 'flowBuilder': return companySettings.enableFlowBuilder === true;
      case 'scheduledMessages': return companySettings.enableScheduledMessages === true;
      case 'campaigns': return companySettings.enableCampaigns === true;
      case 'webhooks': return companySettings.enableWebhooks === true;
      case 'knowledgeBase': return companySettings.enableKnowledgeBase === true;
      case 'aiAgent': return companySettings.enableAIAgent === true;
      case 'integrations': return companySettings.enableIntegrations === true;
      case 'kanban': return companySettings.enableKanban === true;
      default: return true;
    }
  }, [companySettings, isSuperAdmin]);

  const canAccessModule = useCallback((permission: keyof UserPermissions, module?: ModuleType): boolean => {
    const hasUserPermission = canAccess(permission);
    if (!module) return hasUserPermission;
    return hasUserPermission && isModuleEnabled(module);
  }, [canAccess, isModuleEnabled]);

  // Calcula quantos itens de Ferramentas cabem no sidebar colapsado
  useEffect(() => {
    if (sidebarExpanded) {
      setVisibleSecondaryCount(999);
      return;
    }
    const ITEM_H = 42;   // altura de cada item (40px + 2px gap)
    const LABEL_H = 34;  // altura de cada label de seção

    const calculate = () => {
      const nav = sidebarNavRef.current;
      if (!nav) return;
      const totalH = nav.clientHeight;
      // Conta os botões de Principal já renderizados no DOM para ser preciso
      const mainCount = nav.querySelectorAll('[data-section="principal"] button').length || 8;
      const fixedH = 96                              // header colapsado
        + LABEL_H + mainCount * ITEM_H               // Principal
        + LABEL_H                                    // label Ferramentas
        + LABEL_H + 3 * ITEM_H                      // Configurações (tema+som+settings)
        + ITEM_H;                                    // reserva para botão ⋯
      const available = totalH - fixedH + 2 * ITEM_H; // +2 itens extras manualmente
      setVisibleSecondaryCount(Math.max(0, Math.floor(available / ITEM_H)));
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    if (sidebarNavRef.current) observer.observe(sidebarNavRef.current);
    return () => observer.disconnect();
  }, [sidebarExpanded]);

  // Unread tickets counter
  const [unreadCount, setUnreadCount] = useState(0);

  // Unread internal chat messages counter
  const [internalChatUnread, setInternalChatUnread] = useState(0);

  const loadInternalChatUnread = useCallback(async () => {
    try {
      const { data } = await internalChatApi.getUnreadCount();
      setInternalChatUnread(data?.total ?? 0);
    } catch {
      // silently fail
    }
  }, []);

  // Reset badge when landing on /internal-chat
  useEffect(() => {
    if (location.pathname === '/internal-chat') {
      setInternalChatUnread(0);
    }
  }, [location.pathname]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const { data } = await ticketsApi.getAll({ limit: 200 });
      const tickets = data?.tickets || [];
      const unread = tickets.reduce((sum: number, t: any) => sum + (t.unreadMessages || 0), 0) || 0;
      const pendingCount = tickets.filter((t: any) => t.status === 'pending').length || 0;
      setUnreadCount(unread + pendingCount);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  }, []);

  // Pending tasks counter (tasks assigned to current user)
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [unreadTaskCommentsCount, setUnreadTaskCommentsCount] = useState(0);

  // Get viewed task IDs from localStorage
  const getViewedTaskIds = useCallback((): number[] => {
    try {
      const stored = localStorage.getItem(`viewedTasks_${user?.id}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [user?.id]);

  const loadPendingTasksCount = useCallback(async () => {
    try {
      const { data } = await tasksApi.getAll();
      const tasks = data || [];
      const viewedIds = getViewedTaskIds();
      // Count tasks assigned to current user that are not completed and not viewed
      const pendingCount = tasks.filter((t: any) => 
        t.assignedUserId === user?.id && 
        t.status !== 'completed' &&
        !viewedIds.includes(t.id)
      ).length || 0;
      setPendingTasksCount(pendingCount);
      
      // Count unread comments in tasks where user is involved
      let unreadComments = 0;
      const myTasks = tasks.filter((t: any) => 
        (t.assignedUserId === user?.id || t.userId === user?.id) &&
        t.status !== 'completed'
      );
      
      for (const task of myTasks) {
        try {
          const commentsRes = await tasksApi.getComments(task.id);
          const comments = commentsRes.data || [];
          
          // Get last viewed timestamp for this task's comments
          const lastViewedKey = `taskComments_${task.id}_lastViewed_${user?.id}`;
          const lastViewed = localStorage.getItem(lastViewedKey);
          const lastViewedTime = lastViewed ? new Date(lastViewed) : new Date(0);
          
          // Count comments from others that are newer than last viewed
          const newComments = comments.filter((c: any) => 
            c.userId !== user?.id && 
            new Date(c.createdAt) > lastViewedTime
          ).length;
          
          unreadComments += newComments;
        } catch {
          // Ignore errors for individual tasks
        }
      }
      
      setUnreadTaskCommentsCount(unreadComments);
    } catch (err) {
      // Silently fail
    }
  }, [user?.id, getViewedTaskIds]);

  // Profile edit state
  const [profileName, setProfileName] = useState(user?.name || '');
  // Use signature as-is if defined (even if empty), fallback to name only if undefined
  const [profileSignature, setProfileSignature] = useState(user?.signature !== undefined ? user.signature : (user?.name || ''));
  const [profileSignatureEnabled, setProfileSignatureEnabled] = useState(user?.signatureEnabled ?? true);
  const [profilePhone, setProfilePhone] = useState(user?.phone || '');
  const [profileSaving, setProfileSaving] = useState(false);

  // Update profile state when user changes
  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileSignature(user.signature !== undefined && user.signature !== null ? user.signature : (user.name || ''));
      setProfileSignatureEnabled(user.signatureEnabled ?? true);
      setProfilePhone(user.phone || '');
      // Menu sempre aberto se preferência ativada, sempre fechado se desativada
      setSidebarExpanded(user.menuExpanded || false);
    }
  }, [user]);

  const handleSaveProfile = useCallback(async () => {
    try {
      setProfileSaving(true);
      const { data } = await usersApi.updateProfile({
        name: profileName,
        signature: profileSignature,
        signatureEnabled: profileSignatureEnabled,
        phone: profilePhone,
      });
      if (updateUser) {
        updateUser(data);
      }
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setProfileSaving(false);
    }
  }, [profileName, profileSignature, profileSignatureEnabled, profilePhone, updateUser]);

  useEffect(() => {
    loadUnreadCount();
    loadPendingTasksCount();
    loadInternalChatUnread();
    const interval = setInterval(() => {
      loadUnreadCount();
      loadPendingTasksCount();
      loadInternalChatUnread();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount, loadPendingTasksCount, loadInternalChatUnread]);

  useEffect(() => {
    const updateCount = () => {
      void loadUnreadCount();
    };
    wsService.on('new-message', updateCount);
    wsService.on('ticket-created', updateCount);
    wsService.on('ticket-updated', updateCount);

    // Internal chat unread badge
    const handleInternalMsg = () => {
      if (location.pathname !== '/internal-chat') {
        setInternalChatUnread(prev => prev + 1);
      }
    };
    wsService.on('internal-chat-message', handleInternalMsg);
    wsService.on('internal-chat-direct-message', handleInternalMsg);

    return () => {
      wsService.off('new-message', updateCount);
      wsService.off('ticket-created', updateCount);
      wsService.off('ticket-updated', updateCount);
      wsService.off('internal-chat-message', handleInternalMsg);
      wsService.off('internal-chat-direct-message', handleInternalMsg);
    };
  }, [loadUnreadCount]);

  // Listen for task-related WebSocket events
  useEffect(() => {
    const updateTasksCount = () => {
      void loadPendingTasksCount();
    };

    wsService.on('task-created', updateTasksCount);
    wsService.on('task-updated', updateTasksCount);
    wsService.on('task-assigned', updateTasksCount);

    return () => {
      wsService.off('task-created', updateTasksCount);
      wsService.off('task-updated', updateTasksCount);
      wsService.off('task-assigned', updateTasksCount);
    };
  }, [loadPendingTasksCount]);

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.75;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (user?.companyId && !wsService.isConnected()) {
      wsService.connect(user.companyId);
    }
  }, [user]);

  useEffect(() => {
    const playNotification = () => {
      if (!audioRef.current) return;

      // Don't play if user has sound disabled
      if (!soundEnabled) {
        console.log('[Layout] Sound notifications disabled');
        return;
      }

      // Don't play notification sound if an audio message is currently playing
      // This prevents interrupting audio messages with notification sounds
      if (window.__audioMessagePlaying) {
        console.log('[Layout] Skipping notification sound - audio message is playing');
        return;
      }

      try {
        audioRef.current.currentTime = 0;
        void audioRef.current.play();
      } catch (err) {
        console.warn('Failed to play notification sound', err);
      }
    };

    const showBrowserNotification = (title: string, body: string) => {
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: '/favicon.ico',
          tag: 'chatvono-notification',
          requireInteraction: false,
          silent: true,
        });
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
        setTimeout(() => notification.close(), 5000);
      }
    };

    const shouldNotifyForTicket = (data: any): boolean => {
      if (!user?.id) return false;
      const assignedUserId = data?.ticketUserId ?? data?.ticket?.userId ?? data?.ticket?.user?.id;
      if (!assignedUserId) return false;
      return Number(assignedUserId) === Number(user.id);
    };

    const handleNewMessage = (data: any) => {
      if (!shouldNotifyForTicket(data)) return;
      const isTicketsPage = location.pathname.startsWith('/tickets');
      // Extract ticket ID from URL like /tickets/:id
      const ticketIdMatch = location.pathname.match(/\/tickets\/(\d+)/);
      const currentViewingTicketId = ticketIdMatch ? parseInt(ticketIdMatch[1]) : null;
      const messageTicketId = data?.ticketId || data?.ticket?.id;

      // Only play sound if:
      // 1. Not on tickets page OR
      // 2. On tickets page but viewing a different ticket (not the one with new message)
      const shouldNotifySound = !isTicketsPage || (isTicketsPage && currentViewingTicketId !== messageTicketId);

      if (shouldNotifySound) {
        playNotification();
      }

      const contactName = data?.contact?.name || data?.contact?.pushName || t('notifications.newContact');
      const messageBody = data?.body || t('notifications.newMessage');
      showBrowserNotification(t('notifications.newMessageFrom', { name: contactName }), messageBody);
    };

    const handleTicketCreated = (data: any) => {
      if (!shouldNotifyForTicket(data)) return;
      const isTicketsPage = location.pathname.startsWith('/tickets');
      if (isTicketsPage) return;
      if (data?.status === 'pending') {
        playNotification();
        const contactName = data?.contact?.name || data?.contact?.pushName || t('notifications.newContact');
        showBrowserNotification(t('notifications.newPendingTicket'), t('notifications.startedConversation', { name: contactName }));
      }
    };

    const handleFinanceAlert = (data: any) => {
      const title = 'Alerta Financeiro';
      let body = 'Nova atualizacao financeira';
      if (data?.type === 'receivable-overdue') {
        body = 'Cobranca vencida: ' + (data?.description || '');
      }
      if (data?.type === 'payable-overdue') {
        body = 'Conta vencida: ' + (data?.description || '');
      }
      if (data?.type === 'payable-due') {
        body = 'Conta vence hoje: ' + (data?.description || '');
      }
      playNotification();
      showBrowserNotification(title, body);
    };

    const handleFinancePayment = (data: any) => {
      const title = 'Pagamento Registrado';
      const body = data?.amount ? `Valor: R$ ${data.amount}` : 'Pagamento registrado';
      playNotification();
      showBrowserNotification(title, body);
    };

    wsService.on('new-message', handleNewMessage);
    wsService.on('ticket-created', handleTicketCreated);
    wsService.on('finance-alert', handleFinanceAlert);
    wsService.on('finance-payment', handleFinancePayment);

    return () => {
      wsService.off('new-message', handleNewMessage);
      wsService.off('ticket-created', handleTicketCreated);
      wsService.off('finance-alert', handleFinanceAlert);
      wsService.off('finance-payment', handleFinancePayment);
    };
  }, [location.pathname, soundEnabled, t, user?.id]);

  useEffect(() => {
    if (showProfileModal) {
      setTimeout(() => {
        profileNameInputRef.current?.focus();
      }, 50);
    }
  }, [showProfileModal]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const mainMenuItems = useMemo(() => [
    ...(canAccess('canViewDashboard') ? [{ icon: Squares2X2Icon, label: t('nav.dashboard'), path: '/dashboard' }] : []),
    ...(canAccess('canViewTickets') ? [{ icon: ChatBubbleLeftRightIcon, label: t('nav.conversations'), path: '/tickets', badge: unreadCount }] : []),
    ...(canAccessModule('canViewContacts', 'contacts') ? [{ icon: BookOpenIcon, label: t('nav.contacts'), path: '/contacts' }] : []),
    ...(canAccess('canViewQuickReplies') ? [{ icon: SparklesIcon, label: 'Respostas', path: '/quick-replies' }] : []),
    ...(canAccess('canViewInternalChat') ? [{ icon: InboxStackIcon, label: 'Chat Interno', path: '/internal-chat', badge: internalChatUnread }] : []),
    ...(canAccessModule('canViewCRM', 'crm') ? [{ icon: BoltIcon, label: 'CRM', path: '/crm' }] : []),
    ...(isModuleEnabled('kanban') ? [{ icon: ClipboardDocumentListIcon, label: 'Quadros', path: '/quadros' }] : []),
    ...(canAccess('canViewCalendar') ? [{ icon: CalendarIcon, label: 'Calendário', path: '/calendar' }] : []),
  ], [canAccess, canAccessModule, isModuleEnabled, t, unreadCount, internalChatUnread]);

  const secondaryMenuItems = useMemo(() => [
    ...(canAccess('canViewReports') ? [{ icon: DocumentTextIcon, label: 'Relatórios', path: '/reports' }] : []),
    ...(canAccessModule('canViewConnections', 'connections') ? [{ icon: WifiIcon, label: t('nav.connections'), path: '/connections' }] : []),
    ...(canAccessModule('canViewQueues', 'queues') ? [{ icon: ShieldCheckIcon, label: t('nav.queues'), path: '/queues' }] : []),
    ...(canAccessModule('canViewScheduledMessages', 'scheduledMessages') ? [{ icon: CalendarDaysIcon, label: t('nav.scheduled'), path: '/scheduled-messages' }] : []),
    ...(canAccessModule('canViewCampaigns', 'campaigns') ? [{ icon: SpeakerWaveIcon, label: t('nav.campaigns'), path: '/campaigns' }] : []),
    ...(canAccessModule('canViewTags', 'tags') ? [{ icon: BookmarkIcon, label: t('nav.tags'), path: '/tags' }] : []),
    ...(canAccessModule('canViewFlows', 'flowBuilder') ? [{ icon: ArrowsRightLeftIcon, label: t('nav.flowBuilder'), path: '/flows' }] : []),
    ...(canAccessModule('canViewAgents', 'aiAgent') ? [{ icon: CpuChipIcon, label: t('nav.aiAgents'), path: '/ai-agents' }] : []),
    ...(canAccessModule('canViewKnowledgeBase', 'knowledgeBase') ? [{ icon: AcademicCapIcon, label: 'Base de Conhecimento', path: '/knowledge-base' }] : []),
    ...(canAccess('canManageDeals') ? [{ icon: PencilSquareIcon, label: 'Propostas', path: '/proposals' }] : []),
    ...(isSuperAdmin ? [{ icon: BuildingOffice2Icon, label: 'Empresas', path: '/companies' }] : []),
    ...(canAccessModule('canManageIntegrations', 'integrations') ? [{ icon: LinkIcon, label: t('nav.integrations'), path: '/integrations' }] : []),
    ...(canAccessModule('canViewUsers', 'users') ? [{ icon: UserGroupIcon, label: t('nav.users'), path: '/users' }] : []),
    ...(canAccess('canViewFinance') ? [{ icon: CurrencyDollarIcon, label: 'Financeiro', path: '/financial' }] : []),
    ...(isSuperAdmin ? [{ icon: TrophyIcon, label: 'Planos', path: '/plans' }] : []),
    ...(isSuperAdmin ? [{ icon: TagIcon, label: 'Cupons', path: '/coupons' }] : []),
  ], [canAccess, canAccessModule, isModuleEnabled, t, isSuperAdmin]);

  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);

  const handleNavigate = useCallback((path: string) => {
    setShowMoreMenu(false);
    setMobileMenuOpen(false);
    navigate(path);
  }, [navigate]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setShowProfileModal(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--bg-app)' }}>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-4 z-50 md:hidden" style={{ backgroundColor: 'var(--bg-sidebar)', borderBottom: '1px solid var(--border-color)' }}>
        <button
          onClick={() => setShowProfileModal(true)}
          className="rounded-full overflow-hidden"
          aria-label={t('nav.profile')}
        >
          <UserAvatar user={user} size="sm" />
        </button>
        <span className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
          {companySettings?.systemName || 'ChatVono'}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const newVal = !soundEnabled;
              setSoundEnabled(newVal);
              localStorage.setItem('chatvono_sound_enabled', String(newVal));
              if (user?.id) {
                updateUser({ ...user, id: user.id, soundEnabled: newVal });
              }
            }}
            className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
            style={{ color: soundEnabled ? 'var(--wa-green)' : 'var(--text-secondary)' }}
            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
            aria-label={soundEnabled ? 'Mute audio' : 'Unmute audio'}
          >
            {soundEnabled ? (
              <SpeakerWaveIcon className="w-5 h-5" />
            ) : (
              <SpeakerXMarkIcon className="w-5 h-5 opacity-40" />
            )}
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
            style={{ color: 'var(--text-primary)' }}
            aria-label={isDark ? t('theme.light') : t('theme.dark')}
          >
            {isDark ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay + Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              key="mobile-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] md:hidden"
              onClick={closeMobileMenu}
            />
            <motion.aside
              key="mobile-drawer"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              className="fixed bottom-0 left-0 right-0 z-[70] md:hidden flex flex-col rounded-t-3xl shadow-2xl"
              style={{ 
                backgroundColor: 'var(--bg-sidebar)',
                maxHeight: '85vh',
                overflow: 'hidden'
              }}
            >
              <div 
                className="flex flex-col items-center py-3 border-b cursor-pointer active:opacity-70 transition-opacity" 
                style={{ borderColor: 'var(--border-color)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  closeMobileMenu();
                }}
              >
                <div className="w-12 h-1.5 rounded-full mb-2" style={{ backgroundColor: 'var(--border-color)' }} />
                <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Menu Completo</span>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4">
                {/* Grid de ícones principais */}
                <div className="mb-6">
                  <p className="px-2 pb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    Principal
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    {mainMenuItems.map((item) => {
                      const active = isActive(item.path);
                      return (
                        <button
                          key={item.path}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate(item.path);
                          }}
                          className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-all relative"
                          style={{ 
                            backgroundColor: active ? 'var(--wa-green)' : 'var(--bg-hover)',
                            color: active ? 'white' : 'var(--text-primary)'
                          }}
                        >
                          <div className="relative">
                            <item.icon className="h-7 w-7" />
                            {item.badge && item.badge > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white flex items-center justify-center">
                                {item.badge > 9 ? '9+' : item.badge}
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Grid de opções secundárias */}
                {secondaryMenuItems.length > 0 && (
                  <div className="mb-4">
                    <p className="px-2 pb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      Mais Opções
                    </p>
                    <div className="grid grid-cols-4 gap-3">
                      {secondaryMenuItems.map((item) => {
                        const active = isActive(item.path);
                        return (
                          <button
                            key={item.path}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigate(item.path);
                            }}
                            className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-all"
                            style={{ 
                              backgroundColor: active ? 'var(--wa-green)' : 'var(--bg-hover)',
                              color: active ? 'white' : 'var(--text-primary)'
                            }}
                          >
                            <item.icon className="h-7 w-7" />
                            <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ações adicionais em cards */}
                <div className="space-y-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <LanguageSwitcher expanded={true} />

                  {canAccess('canViewSettings') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate('/settings');
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive('/settings') ? 'bg-[var(--wa-green)]/10' : 'hover:bg-[var(--bg-hover)]'}`}
                      style={{ color: isActive('/settings') ? 'var(--wa-green)' : 'var(--text-primary)' }}
                    >
                      <Cog6ToothIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">{t('nav.settings')}</span>
                    </button>
                  )}

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 transition-all"
                    style={{ color: 'var(--danger)' }}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">{t('nav.logout')}</span>
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside
        className={`wa-icon-sidebar hidden md:flex ${sidebarExpanded ? 'expanded' : ''}`}
        style={{
          width: sidebarExpanded ? '260px' : '72px',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
        }}
      >
        {/* ── Header com avatar + nome + chevron ──────── */}
        <div
          style={{
            padding: sidebarExpanded ? '20px 16px 14px' : '16px 0 12px',
            borderBottom: '1px solid var(--border-color)',
            flexShrink: 0,
          }}
        >
          {sidebarExpanded ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex-shrink-0 hover:opacity-80 transition-opacity rounded-full overflow-hidden"
                title="Meu perfil"
              >
                <UserAvatar user={user} size="md" />
              </button>
              <div className="flex flex-col overflow-hidden flex-1 min-w-0">
                <span
                  className="text-[10px] font-semibold uppercase tracking-widest truncate"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {user?.role === 'admin' ? 'Administrador' : user?.role === 'supervisor' ? 'Supervisor' : 'Agente'}
                </span>
                <span className="text-sm font-bold truncate leading-snug" style={{ color: 'var(--text-primary)' }}>
                  {user?.name || 'Usuário'}
                </span>
              </div>
              <button
                onClick={handleToggleSidebar}
                title="Recolher menu"
                className="flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                style={{ width: '28px', height: '28px', color: 'var(--text-secondary)' }}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={() => setShowProfileModal(true)}
                className="hover:opacity-80 transition-opacity rounded-full overflow-hidden"
                title="Meu perfil"
              >
                <UserAvatar user={user} size="sm" />
              </button>
              <button
                onClick={handleToggleSidebar}
                title="Expandir menu"
                className="flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                style={{ width: '28px', height: '28px', color: 'var(--text-secondary)' }}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* ── Navegação unificada ─────────────────────── */}
        <div
          ref={sidebarNavRef}
          className="flex flex-col"
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0 8px',
          }}
        >

          {/* ── Seção: Principal ───────────────────────── */}
          <p
            className="px-2 font-semibold uppercase tracking-widest"
            style={{
              color: 'var(--text-secondary)',
              fontSize: '10px',
              padding: sidebarExpanded ? '16px 8px 6px' : '14px 0 4px',
              textAlign: sidebarExpanded ? 'left' : 'center',
              letterSpacing: sidebarExpanded ? '0.1em' : '0',
            }}
          >
            {sidebarExpanded ? 'Principal' : '·'}
          </p>

          <div data-section="principal">
          {mainMenuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <SidebarTooltip key={item.path} label={item.label} show={!sidebarExpanded}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  className="wa-icon-btn"
                  style={{
                    width: '100%',
                    justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                    padding: sidebarExpanded ? '9px 12px' : '10px 0',
                    borderRadius: '9px',
                    minHeight: '40px',
                    gap: '10px',
                    backgroundColor: active ? 'color-mix(in srgb, var(--wa-green) 10%, transparent)' : 'transparent',
                    color: active ? 'var(--wa-green)' : 'var(--text-secondary)',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {/* Ícone isolado num wrapper para o badge não desalinhar */}
                  <span className="relative flex-shrink-0 flex items-center justify-center" style={{ width: '19px', height: '19px' }}>
                    <item.icon className="h-[19px] w-[19px]" />
                    {(item.badge ?? 0) > 0 && (
                      <span
                        className="absolute flex items-center justify-center text-white font-bold rounded-full"
                        style={{
                          fontSize: '9px',
                          minWidth: '14px',
                          height: '14px',
                          padding: '0 3px',
                          backgroundColor: 'var(--wa-green)',
                          top: '-5px',
                          right: '-7px',
                          lineHeight: 1,
                        }}
                      >
                        {(item.badge ?? 0) > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </span>
                  {sidebarExpanded && (
                    <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis" style={{ flex: 1, textAlign: 'left' }}>
                      {item.label}
                    </span>
                  )}
                </button>
              </SidebarTooltip>
            );
          })}
          </div>

          {/* ── Seção: Ferramentas ─────────────────────── */}
          {(sidebarExpanded ? secondaryMenuItems.length > 0 : true) && (
            <p
              className="font-semibold uppercase tracking-widest"
              style={{
                color: 'var(--text-secondary)',
                fontSize: '10px',
                padding: sidebarExpanded ? '16px 8px 6px' : '14px 0 4px',
                textAlign: sidebarExpanded ? 'left' : 'center',
              }}
            >
              {sidebarExpanded ? 'Ferramentas' : '·'}
            </p>
          )}

          {/* Ferramentas — expanded: todos, collapsed: até visibleSecondaryCount + flyout ⋯ */}
          {(() => {
            const visible = sidebarExpanded
              ? secondaryMenuItems
              : secondaryMenuItems.slice(0, visibleSecondaryCount);
            const overflow = sidebarExpanded
              ? []
              : secondaryMenuItems.slice(visibleSecondaryCount);
            return (
              <>
                {visible.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <SidebarTooltip key={item.path} label={item.label} show={!sidebarExpanded}>
                      <button
                        onClick={() => handleNavigate(item.path)}
                        className="wa-icon-btn"
                        style={{
                          width: '100%',
                          justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                          padding: sidebarExpanded ? '9px 12px' : '10px 0',
                          borderRadius: '9px',
                          minHeight: '40px',
                          gap: '10px',
                          backgroundColor: active ? 'color-mix(in srgb, var(--wa-green) 10%, transparent)' : 'transparent',
                          color: active ? 'var(--wa-green)' : 'var(--text-secondary)',
                          fontWeight: active ? 600 : 500,
                        }}
                      >
                        <item.icon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />
                        {sidebarExpanded && (
                          <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis" style={{ flex: 1, textAlign: 'left' }}>
                            {item.label}
                          </span>
                        )}
                      </button>
                    </SidebarTooltip>
                  );
                })}

                {overflow.length > 0 && (
                  <SidebarTooltip label="Mais opções" show>
                    <button
                      ref={overflowMenuButtonRef}
                      onClick={() => setShowOverflowMenu((v) => !v)}
                      className={`wa-icon-btn ${showOverflowMenu ? 'active' : ''}`}
                      style={{ width: '100%', justifyContent: 'center', padding: '10px 0', borderRadius: '9px', minHeight: '40px' }}
                    >
                      <EllipsisVerticalIcon className="h-[19px] w-[19px]" />
                    </button>
                  </SidebarTooltip>
                )}

                {showOverflowMenu && overflow.length > 0 && createPortal(
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex: 999999, backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
                    onClick={() => setShowOverflowMenu(false)}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.92, y: 16 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.92, y: 16 }}
                      transition={{ duration: 0.18 }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
                      style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)', margin: '0 16px' }}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Ferramentas</span>
                        <button
                          onClick={() => setShowOverflowMenu(false)}
                          className="flex items-center justify-center rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
                          style={{ width: '28px', height: '28px', color: 'var(--text-secondary)' }}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {/* Grid */}
                      <div className="grid grid-cols-4 gap-3 p-5">
                        {overflow.map((item) => {
                          const active = isActive(item.path);
                          return (
                            <button
                              key={item.path}
                              onClick={() => { setShowOverflowMenu(false); handleNavigate(item.path); }}
                              className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-all"
                              style={{
                                backgroundColor: active ? 'var(--wa-green)' : 'var(--bg-hover)',
                                color: active ? 'white' : 'var(--text-primary)',
                              }}
                            >
                              <item.icon className="h-6 w-6" />
                              <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </motion.div>,
                  document.body
                )}
              </>
            );
          })()}

          {/* ── Seção: Configurações ───────────────────── */}
          <p
            className="font-semibold uppercase tracking-widest"
            style={{
              color: 'var(--text-secondary)',
              fontSize: '10px',
              padding: sidebarExpanded ? '16px 8px 6px' : '14px 0 4px',
              textAlign: sidebarExpanded ? 'left' : 'center',
            }}
          >
            {sidebarExpanded ? 'Configurações' : '·'}
          </p>

          {/* Tema */}
          <SidebarTooltip label={isDark ? 'Tema claro' : 'Tema escuro'} show={!sidebarExpanded}>
            <button
              onClick={toggleTheme}
              className="wa-icon-btn"
              style={{
                width: '100%',
                justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                padding: sidebarExpanded ? '9px 12px' : '10px 0',
                borderRadius: '9px',
                minHeight: '40px',
                gap: '10px',
                color: 'var(--text-secondary)',
              }}
            >
              {isDark
                ? <SunIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />
                : <MoonIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />}
              {sidebarExpanded && (
                <span className="text-sm font-medium whitespace-nowrap">
                  {isDark ? 'Tema claro' : 'Tema escuro'}
                </span>
              )}
            </button>
          </SidebarTooltip>

          {/* Som */}
          <SidebarTooltip label={soundEnabled ? 'Desativar som' : 'Ativar som'} show={!sidebarExpanded}>
            <button
              onClick={() => {
                const newVal = !soundEnabled;
                setSoundEnabled(newVal);
                localStorage.setItem('chatvono_sound_enabled', String(newVal));
                if (user?.id) updateUser({ ...user, id: user.id, soundEnabled: newVal });
              }}
              className="wa-icon-btn"
              style={{
                width: '100%',
                justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                padding: sidebarExpanded ? '9px 12px' : '10px 0',
                borderRadius: '9px',
                minHeight: '40px',
                gap: '10px',
                color: soundEnabled ? 'var(--wa-green)' : 'var(--text-secondary)',
              }}
            >
              {soundEnabled
                ? <SpeakerWaveIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />
                : <SpeakerXMarkIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />}
              {sidebarExpanded && (
                <span className="text-sm font-medium whitespace-nowrap">
                  {soundEnabled ? 'Som ativado' : 'Som desativado'}
                </span>
              )}
            </button>
          </SidebarTooltip>

          {/* Configurações da empresa */}
          {canAccess('canViewSettings') && (
            <SidebarTooltip label={t('nav.settings')} show={!sidebarExpanded}>
              <button
                onClick={() => navigate('/settings')}
                className="wa-icon-btn"
                style={{
                  width: '100%',
                  justifyContent: sidebarExpanded ? 'flex-start' : 'center',
                  padding: sidebarExpanded ? '9px 12px' : '10px 0',
                  borderRadius: '9px',
                  minHeight: '40px',
                  gap: '10px',
                  backgroundColor: isActive('/settings') ? 'color-mix(in srgb, var(--wa-green) 10%, transparent)' : 'transparent',
                  color: isActive('/settings') ? 'var(--wa-green)' : 'var(--text-secondary)',
                  fontWeight: isActive('/settings') ? 600 : 500,
                }}
              >
                <Cog6ToothIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />
                {sidebarExpanded && (
                  <span className="text-sm whitespace-nowrap">
                    {t('nav.settings')}
                  </span>
                )}
              </button>
            </SidebarTooltip>
          )}

          {/* Sair — apenas expandido */}
          {sidebarExpanded && (
            <button
              onClick={handleLogout}
              className="wa-icon-btn"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                padding: '9px 12px',
                borderRadius: '9px',
                minHeight: '40px',
                gap: '10px',
                color: 'var(--danger)',
                marginBottom: '16px',
              }}
            >
              <ArrowRightOnRectangleIcon className="h-[19px] w-[19px]" style={{ flexShrink: 0 }} />
              <span className="text-sm font-medium whitespace-nowrap" style={{ flex: 1 }}>
                {t('nav.logout')}
              </span>
            </button>
          )}

        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-14 pb-20 md:pt-0 md:pb-0" style={{ backgroundColor: 'var(--bg-sidebar)' }}>
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 h-16 flex items-center justify-around px-2 z-50 md:hidden border-t" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
        {mainMenuItems.slice(0, 3).map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all relative min-w-[60px]"
              style={{ color: active ? 'var(--wa-green)' : 'var(--text-muted)' }}
            >
              <div className="relative">
                <item.icon className="h-6 w-6" />
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-[var(--wa-green)] text-white flex items-center justify-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          );
        })}
        
        {mainMenuItems.length > 3 && mainMenuItems.slice(3, 4).map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => handleNavigate(item.path)}
              className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all relative min-w-[60px]"
              style={{ color: active ? 'var(--wa-green)' : 'var(--text-muted)' }}
            >
              <div className="relative">
                <item.icon className="h-6 w-6" />
                {item.badge && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-[var(--wa-green)] text-white flex items-center justify-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          );
        })}
        
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-all min-w-[60px]"
          style={{ color: 'var(--text-muted)' }}
        >
          <Menu className="h-6 w-6" />
          <span className="text-[10px] font-medium">Menu</span>
        </button>
      </div>

      {/* Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
            onClick={() => setShowProfileModal(false)}
            role="dialog"
            aria-modal="true"
            aria-label={t('profile.title')}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl shadow-2xl border overflow-hidden"
              style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center gap-3">
                  <UserAvatar user={user} size="md" />
                  <div>
                    <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{t('profile.title')}</h2>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="p-2 rounded-full transition-colors hover:bg-gray-500/10"
                  style={{ color: 'var(--text-muted)' }}
                  aria-label={t('button.close')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                    {t('form.name')}
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                      ref={profileNameInputRef}
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{
                        backgroundColor: 'var(--bg-input)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}
                      placeholder={t('profile.namePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                    {t('form.phone')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{
                        backgroundColor: 'var(--bg-input)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                      }}
                      placeholder="+55 61 98251-1308"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {t('profile.signature')}
                    </label>
                    <button
                      onClick={() => setProfileSignatureEnabled(!profileSignatureEnabled)}
                      className="relative w-11 h-6 rounded-full transition-colors"
                      style={{ backgroundColor: profileSignatureEnabled ? 'var(--wa-green)' : 'var(--border-color)' }}
                      aria-pressed={profileSignatureEnabled}
                      aria-label={t('profile.signature')}
                    >
                      <div
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform"
                        style={{ transform: profileSignatureEnabled ? 'translateX(1.25rem)' : 'translateX(0.25rem)' }}
                      />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={profileSignature}
                    onChange={(e) => setProfileSignature(e.target.value)}
                    disabled={!profileSignatureEnabled}
                    className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--bg-input)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                    placeholder={t('profile.signaturePlaceholder')}
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    {t('profile.signatureHelp')}
                  </p>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-input)' }}>
                  <ShieldCheck className="w-4 h-4" style={{ color: 'var(--wa-green)' }} />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('profile.role')}:</span>
                  <span
                    className="text-sm font-medium px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--wa-green)',
                      color: 'white',
                    }}
                  >
                    {user?.role === 'admin' ? t('roles.admin') :
                     user?.role === 'supervisor' ? t('roles.supervisor') :
                     user?.role === 'agent' ? t('roles.agent') : t('roles.agent')}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t" style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border-color)' }}>
                <button
                  onClick={() => setShowProfileModal(false)}
                  disabled={profileSaving}
                  className="flex-1 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 hover:bg-gray-500/10"
                  style={{
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {t('button.cancel')}
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2 hover:opacity-90"
                  style={{ backgroundColor: 'var(--wa-green)' }}
                >
                  {profileSaving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {t('button.save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
